import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'leaflet/dist/leaflet.css';

function LocationPicker({ initialLocation = null, onLocationChange }) {
  const [address, setAddress] = useState(initialLocation?.address || '');
  const [coordinates, setCoordinates] = useState({
    lat: initialLocation?.lat || null,
    lng: initialLocation?.lng || null
  });
  const [mapInitialized, setMapInitialized] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Define reverseGeocode with useCallback
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      // In a real app, this would call a reverse geocoding API
      // For example:
      // const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=YOUR_TOKEN&language=en`);
      // const data = await response.json();
      // const formattedAddress = data.features[0].place_name;
      
      // For now, use a simple formatted string with coordinates
      const formattedAddress = `Location near (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
      setAddress(formattedAddress);
    } catch (err) {
      console.error('Reverse geocoding error:', err);
    }
  }, []);

  // Initialize map when component mounts
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Dynamic import for Leaflet
    import('leaflet').then(L => {
      if (mapRef.current) return;  // Don't initialize twice
      
      // Create map centered on Nairobi if no initial location
      const defaultCenter = [-1.2921, 36.8219]; // Nairobi coordinates
      const center = coordinates.lat && coordinates.lng 
        ? [coordinates.lat, coordinates.lng] 
        : defaultCenter;
      
      // Initialize map
      mapRef.current = L.map(mapContainerRef.current, {
        center,
        zoom: 13,
        scrollWheelZoom: true
      });

      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(mapRef.current);

      // Add marker if we have coordinates
      if (coordinates.lat && coordinates.lng) {
        markerRef.current = L.marker([coordinates.lat, coordinates.lng], {
          draggable: true
        }).addTo(mapRef.current);
        
        // Update coordinates when marker is moved
        markerRef.current.on('dragend', function(e) {
          const position = e.target.getLatLng();
          setCoordinates({
            lat: position.lat,
            lng: position.lng
          });
          
          // Convert coordinates to address
          reverseGeocode(position.lat, position.lng);
          
          // Call parent callback
          if (onLocationChange) {
            onLocationChange({
              address,
              lat: position.lat,
              lng: position.lng
            });
          }
        });
      }

      // Add click handler to map
      mapRef.current.on('click', function(e) {
        const { lat, lng } = e.latlng;
        
        // Update coordinates
        setCoordinates({ lat, lng });
        
        // Add or move marker
        if (markerRef.current) {
          markerRef.current.setLatLng([lat, lng]);
        } else {
          markerRef.current = L.marker([lat, lng], {
            draggable: true
          }).addTo(mapRef.current);
          
          // Update coordinates when marker is moved
          markerRef.current.on('dragend', function(e) {
            const position = e.target.getLatLng();
            setCoordinates({
              lat: position.lat,
              lng: position.lng
            });
            
            // Convert coordinates to address
            reverseGeocode(position.lat, position.lng);
            
            // Call parent callback
            if (onLocationChange) {
              onLocationChange({
                address,
                lat: position.lat,
                lng: position.lng
              });
            }
          });
        }
        
        // Convert coordinates to address
        reverseGeocode(lat, lng);
        
        // Call parent callback
        if (onLocationChange) {
          onLocationChange({
            address,
            lat,
            lng
          });
        }
      });

      setMapInitialized(true);

      // Cleanup on unmount
      return () => {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
          markerRef.current = null;
        }
      };
    });
  }, [coordinates.lat, coordinates.lng, address, onLocationChange, reverseGeocode]);

  // Handle address input change
  const handleAddressChange = (e) => {
    setAddress(e.target.value);
    setError('');
  };

  // Geocode address to get coordinates
  const geocodeAddress = async () => {
    if (!address.trim()) {
      setError('Please enter an address');
      return;
    }

    setSearching(true);
    setError('');

    try {
      // In a real app, this would call a geocoding API like Mapbox, Google Maps, etc.
      // For this example, we'll simulate a geocoding response
      
      // Normally would be:
      // const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=YOUR_TOKEN&country=ke`);
      // const data = await response.json();
      
      // Simulate an API response delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // If address contains "Nairobi", simulate a successful geocode
      if (address.toLowerCase().includes('nairobi')) {
        // Simulate different coordinates based on input to make it seem realistic
        const randomOffset = (Math.random() - 0.5) * 0.05;
        const newCoordinates = {
          lat: -1.2921 + randomOffset,
          lng: 36.8219 + randomOffset
        };
        
        setCoordinates(newCoordinates);
        
        // Update map view and marker
        if (mapRef.current) {
          mapRef.current.setView([newCoordinates.lat, newCoordinates.lng], 15);
          
          if (markerRef.current) {
            markerRef.current.setLatLng([newCoordinates.lat, newCoordinates.lng]);
          } else {
            import('leaflet').then(L => {
              markerRef.current = L.marker([newCoordinates.lat, newCoordinates.lng], {
                draggable: true
              }).addTo(mapRef.current);
              
              // Update coordinates when marker is moved
              markerRef.current.on('dragend', function(e) {
                const position = e.target.getLatLng();
                setCoordinates({
                  lat: position.lat,
                  lng: position.lng
                });
                
                // Convert coordinates to address
                reverseGeocode(position.lat, position.lng);
                
                // Call parent callback
                if (onLocationChange) {
                  onLocationChange({
                    address,
                    lat: position.lat,
                    lng: position.lng
                  });
                }
              });
            });
          }
        }
        
        // Call parent callback
        if (onLocationChange) {
          onLocationChange({
            address,
            lat: newCoordinates.lat,
            lng: newCoordinates.lng
          });
        }
      } else {
        setError('Location not found. Please try a different address.');
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setError('Error finding location. Please try again.');
    } finally {
      setSearching(false);
    }
  };



  return (
    <div>
      <div className="mb-4">
        <label htmlFor="address" className="block text-sm font-medium text-gray-700">
          Address
        </label>
        <div className="mt-1 flex rounded-md shadow-sm">
          <input
            type="text"
            name="address"
            id="address"
            value={address}
            onChange={handleAddressChange}
            className={`block w-full rounded-none rounded-l-md shadow-sm sm:text-sm ${
              error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
            placeholder="Enter an address or location"
          />
          <button
            type="button"
            onClick={geocodeAddress}
            disabled={searching}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-r-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {searching ? (
              <svg className="animate-spin h-4 w-4 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Search'
            )}
          </button>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Enter an address or select a location on the map
        </p>
      </div>
      
      <div 
        ref={mapContainerRef} 
        className="h-64 w-full rounded-md shadow-sm border border-gray-300 bg-gray-100"
      >
        {!mapInitialized && (
          <div className="h-full w-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        )}
      </div>
      
      <div className="mt-2 text-xs text-gray-500 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Click on the map to pick a location or search by address
      </div>
      
      {coordinates.lat && coordinates.lng && (
        <div className="mt-2 text-xs text-gray-600">
          Coordinates: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
        </div>
      )}
    </div>
  );
}

export default LocationPicker;
